'use client';

// ⚠️ SENSITIVE FILE — Chart rendering & real-time data pipeline
// Changes here directly affect what users see on market charts (consensus, live prices, candles).
// If using AI assistance, MANUALLY TEST all chart variants before committing:
//   - Consensus mode (market share %)
//   - Live prices mode (line + candle)
//   - Pre-trade (new market, no data)
//   - During confirmation (resolving banner + pulsing line)
//   - Post-resolution (winner continues, loser clamped to 0)
//   - Mode switching (consensus ↔ live prices without "No data" flash)
//   - Interval switching (1m, 5m, 15m, 1h, 4h, 1d, All)
import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedSwitch } from '@/components/ui/animated-switch';
import { Separator } from '@/components/ui/app-separator';
import { HelpCircleIcon } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';
import { useChartSettingsStore } from '@/stores/chart-settings';

// ---------------------------------------------------------------------------
// Toggle setting definition
// ---------------------------------------------------------------------------

type ToggleKey = 'showBoosts' | 'showTrades' | 'showBreakEven' | 'showXAxis' | 'showYAxis' | 'showPriceLines' | 'alwaysShowTooltip' | 'scaleMarketShareOnHover';
type SettingLabelKey = 'setting_boosts' | 'setting_trades' | 'setting_break_even' | 'setting_x_axis' | 'setting_y_axis' | 'setting_price_lines' | 'setting_always_show_tooltip' | 'setting_scale_market_share_on_hover';
type SectionTitleKey = 'settings_section_markers' | 'settings_section_axes' | 'settings_section_tooltip';
interface ToggleSetting {
  key: ToggleKey;
  labelKey: SettingLabelKey;
}
const MARKER_TOGGLES: readonly ToggleSetting[] = [{
  key: 'showTrades',
  labelKey: 'setting_trades'
}, {
  key: 'showBoosts',
  labelKey: 'setting_boosts'
}, {
  key: 'showBreakEven',
  labelKey: 'setting_break_even'
}] as const;
const AXIS_TOGGLES: readonly ToggleSetting[] = [{
  key: 'showXAxis',
  labelKey: 'setting_x_axis'
}, {
  key: 'showYAxis',
  labelKey: 'setting_y_axis'
}, {
  key: 'showPriceLines',
  labelKey: 'setting_price_lines'
}, {
  key: 'scaleMarketShareOnHover',
  labelKey: 'setting_scale_market_share_on_hover'
}] as const;
const TOOLTIP_TOGGLES: readonly ToggleSetting[] = [{
  key: 'alwaysShowTooltip',
  labelKey: 'setting_always_show_tooltip'
}] as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ChartSettingsPanelProps {
  className?: string;
  onOpenChartGuide: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Module-scope (not nested in ChartSettingsPanel) so the React Compiler can
// optimize it and it never resets state on a parent re-render.
function ToggleSection(t0) {
  const $ = _c(19);
  const {
    titleKey,
    toggles,
    settingValues,
    toggleSetting
  } = t0;
  const t = useTranslations("chart");
  let t1;
  if ($[0] !== t || $[1] !== titleKey) {
    t1 = t(titleKey);
    $[0] = t;
    $[1] = titleKey;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="text-caption font-medium text-foreground-secondary">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== settingValues || $[6] !== t || $[7] !== toggleSetting || $[8] !== toggles) {
    let t4;
    if ($[10] !== settingValues || $[11] !== t || $[12] !== toggleSetting) {
      t4 = toggle => {
        const switchId = `chart-setting-${toggle.key}`;
        return <div key={toggle.key} className="flex items-center justify-between gap-2 text-body"><label htmlFor={switchId}>{t(toggle.labelKey)}</label><AnimatedSwitch id={switchId} size="sm" checked={settingValues[toggle.key]} onCheckedChange={() => toggleSetting(toggle.key)} /></div>;
      };
      $[10] = settingValues;
      $[11] = t;
      $[12] = toggleSetting;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = toggles.map(t4);
    $[5] = settingValues;
    $[6] = t;
    $[7] = toggleSetting;
    $[8] = toggles;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[14] !== t3) {
    t4 = <div className="flex flex-col gap-2">{t3}</div>;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t2 || $[17] !== t4) {
    t5 = <div className="flex flex-col gap-1">{t2}{t4}</div>;
    $[16] = t2;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
export function ChartSettingsPanel(t0) {
  const $ = _c(31);
  const {
    className,
    onOpenChartGuide
  } = t0;
  const t = useTranslations("chart");
  const showBoosts = useChartSettingsStore(_temp);
  const showTrades = useChartSettingsStore(_temp2);
  const showBreakEven = useChartSettingsStore(_temp3);
  const showXAxis = useChartSettingsStore(_temp4);
  const showYAxis = useChartSettingsStore(_temp5);
  const showPriceLines = useChartSettingsStore(_temp6);
  const alwaysShowTooltip = useChartSettingsStore(_temp7);
  const scaleMarketShareOnHover = useChartSettingsStore(_temp8);
  const toggleSetting = useChartSettingsStore(_temp9);
  let t1;
  if ($[0] !== alwaysShowTooltip || $[1] !== scaleMarketShareOnHover || $[2] !== showBoosts || $[3] !== showBreakEven || $[4] !== showPriceLines || $[5] !== showTrades || $[6] !== showXAxis || $[7] !== showYAxis) {
    t1 = {
      showBoosts,
      showTrades,
      showBreakEven,
      showXAxis,
      showYAxis,
      showPriceLines,
      alwaysShowTooltip,
      scaleMarketShareOnHover
    };
    $[0] = alwaysShowTooltip;
    $[1] = scaleMarketShareOnHover;
    $[2] = showBoosts;
    $[3] = showBreakEven;
    $[4] = showPriceLines;
    $[5] = showTrades;
    $[6] = showXAxis;
    $[7] = showYAxis;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const settingValues = t1;
  let t2;
  if ($[9] !== className) {
    t2 = cn("flex w-full flex-col gap-4", className);
    $[9] = className;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  let t4;
  let t5;
  if ($[11] !== settingValues || $[12] !== toggleSetting) {
    t3 = <ToggleSection titleKey="settings_section_markers" toggles={MARKER_TOGGLES} settingValues={settingValues} toggleSetting={toggleSetting} />;
    t4 = <ToggleSection titleKey="settings_section_axes" toggles={AXIS_TOGGLES} settingValues={settingValues} toggleSetting={toggleSetting} />;
    t5 = <ToggleSection titleKey="settings_section_tooltip" toggles={TOOLTIP_TOGGLES} settingValues={settingValues} toggleSetting={toggleSetting} />;
    $[11] = settingValues;
    $[12] = toggleSetting;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Separator />;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t) {
    t7 = t("settings_chart_guide");
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t) {
    t8 = t("settings_chart_guide");
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== onOpenChartGuide || $[22] !== t7 || $[23] !== t8) {
    t9 = <AnimatedButton variant="tertiary" size="sm" prefix={HelpCircleIcon} aria-label={t7} onClick={onOpenChartGuide} className="w-full">{t8}</AnimatedButton>;
    $[21] = onOpenChartGuide;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t2 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t9) {
    t10 = <div className={t2}>{t3}{t4}{t5}{t6}{t9}</div>;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
}
function _temp9(s_7) {
  return s_7.toggleSetting;
}
function _temp8(s_6) {
  return s_6.scaleMarketShareOnHover;
}
function _temp7(s_5) {
  return s_5.alwaysShowTooltip;
}
function _temp6(s_4) {
  return s_4.showPriceLines;
}
function _temp5(s_3) {
  return s_3.showYAxis;
}
function _temp4(s_2) {
  return s_2.showXAxis;
}
function _temp3(s_1) {
  return s_1.showBreakEven;
}
function _temp2(s_0) {
  return s_0.showTrades;
}
function _temp(s) {
  return s.showBoosts;
}